import { Type, Loc, Dom, Event, Runtime, Text, Browser, Uri, Tag, Easing, Reflection, type JsonObject } from 'main.core';
import { BaseEvent, EventEmitter } from 'main.core.events';
import { MemoryCache } from 'main.core.cache';
import { ZIndexManager, type ZIndexComponent } from 'main.core.z-index-manager';
import { FocusTrap, FocusMonitor, type FocusTrapOptions } from 'ui.a11y';
import { type Popup } from 'main.popup';
import { renderSkeleton } from 'ui.system.skeleton';
import 'clipboard';

import { Dictionary } from './dictionary';
import { Label } from './label';
import { MessageEvent } from './message-event';
import { SliderEvent } from './slider-event';

import { type SliderOptions, type SliderEvents } from './types/slider-options';
import { type MinimizeOptions } from './types/minimize-options';
import { type OuterBoundary } from './types/outer-boundary';

export class Slider
{
	#refs: MemoryCache<HTMLElement> = new MemoryCache<HTMLElement>();
	#options: SliderOptions;

	#startPosition: 'right' | 'bottom' | 'top' = 'right';
	#startAnimationState: Record<string, number> | null = null;
	#endAnimationState: Record<string, number> | null = null;
	#currentAnimationState: Record<string, number> | null = null;
	#outerBoundary: OuterBoundary = {};
	#hideToolbarOnOpen: boolean = false;
	#designSystemContext: string = '--ui-context-content-light';
	#zIndexComponent: ZIndexComponent | null = null;
	#autoOffset: boolean = true;
	#focusTrap: FocusTrap | null = null;
	#targetContainer: HTMLElement | string | null = null;

	private url: string;
	private offset: number | null = null;
	private width: number | null = null;
	private title: string | null = null;
	private data: Dictionary;

	private contentCallback: Function | null = null;
	private contentCallbackInvoved: boolean = false;
	private contentClassName: string | null = null;
	private containerClassName: string | null = null;
	private overlayClassName: string | null = null;

	private hideControls: boolean = false;
	private cacheable: boolean = true;
	private autoFocus: boolean = true;
	private printable: boolean = false;
	private allowChangeHistory: boolean | null = null;
	private allowChangeTitle: boolean | null = null;
	private allowCrossOrigin: boolean = false;

	private customLeftBoundary: number | null = null;
	private customRightBoundary: number | null = null;

	private iframe: HTMLIFrameElement | null = null;
	private iframeSrc: string | null = null;
	private iframeId: string | null = null;
	private requestMethod: 'get' | 'post' = 'get';
	private requestParams: JsonObject = {};

	private opened: boolean = false;
	private hidden: boolean = false;
	private destroyed: boolean = false;
	private loaded: boolean = false;
	private loadedCnt: number = 0;
	private minimizing: boolean = false;
	private maximizing: boolean = false;

	private layout: {
		overlay: HTMLElement | null;
		container: HTMLElement | null;
		loader: HTMLElement | null;
		content: HTMLElement | null;
		closeBtn?: HTMLElement | null;
	};

	private skeleton: string | undefined;
	private loader: string | HTMLElement;

	private animation: Easing | null = null;
	private animationDuration: number = 200;
	private animationName: 'sliding' | 'scale' = 'sliding';
	private animationOptions: { origin?: string } = {};

	private overlayBgColor: string = '#000000';
	private overlayOpacity: number = 40;
	private overlayBgCallback: Function | null = null;
	private overlayAnimation: boolean = false;

	private minimizeOptions: MinimizeOptions | null = null;

	private label: Label;
	private minimizeLabel: Label;
	private newWindowLabel: Label | null = null;
	private copyLinkLabel: Label | null = null;
	private printLabel: Label | null = null;

	constructor(url: string, sliderOptions: SliderOptions)
	{
		const options: SliderOptions = Type.isPlainObject(sliderOptions) ? sliderOptions : {};
		this.#options = options;

		this.contentCallback = Type.isFunction(options.contentCallback) ? options.contentCallback : null;
		this.contentCallbackInvoved = false;
		this.contentClassName = Type.isStringFilled(options.contentClassName) ? options.contentClassName : null;
		this.containerClassName = Type.isStringFilled(options.containerClassName) ? options.containerClassName : null;
		this.overlayClassName = Type.isStringFilled(options.overlayClassName) ? options.overlayClassName : null;

		this.url = this.contentCallback ? url : this.refineUrl(url);

		this.offset = null;
		this.hideControls = options.hideControls === true;
		this.width = Type.isNumber(options.width) ? options.width : null;
		this.cacheable = options.cacheable !== false;
		this.autoFocus = options.autoFocus !== false;
		this.printable = options.printable === true;
		this.allowChangeHistory = Type.isBoolean(options.allowChangeHistory) ? options.allowChangeHistory : null;
		this.allowChangeTitle = Type.isBoolean(options.allowChangeTitle) ? options.allowChangeTitle : null;
		this.allowCrossOrigin = options.allowCrossOrigin === true;
		this.data = new Dictionary(Type.isPlainObject(options.data) ? options.data : {});

		this.customLeftBoundary = null;
		this.customRightBoundary = null;
		this.setCustomLeftBoundary(options.customLeftBoundary);
		this.setCustomRightBoundary(options.customRightBoundary);

		this.title = null;
		this.setTitle(options.title);
		/**
		 *
		 * @type {HTMLIFrameElement}
		 */
		this.iframe = null;
		this.iframeSrc = null;
		this.iframeId = null;
		this.requestMethod = Type.isStringFilled(options.requestMethod) && options.requestMethod.toLowerCase() === 'post'
			? 'post'
			: 'get';

		this.requestParams = Type.isPlainObject(options.requestParams) ? options.requestParams : {};

		this.opened = false;
		this.hidden = false;
		this.destroyed = false;
		this.loaded = false;
		this.loadedCnt = 0;

		this.minimizing = false;
		this.maximizing = false;

		this.layout = {
			overlay: null,
			container: null,
			loader: null,
			content: null,
		};

		this.skeleton = options.skeleton;

		this.loader = Type.isStringFilled(options.loader) || Type.isElementNode(options.loader)
			? options.loader
			: (Type.isStringFilled(options.typeLoader)
				? options.typeLoader
				: 'default-loader');

		this.animation = null;
		this.animationDuration = Type.isNumber(options.animationDuration) ? options.animationDuration : 200;
		this.overlayBgColor = Type.isStringFilled(options.overlayBgColor) && /^#[\dA-Za-f]{6}$/.test(options.overlayBgColor)
			? options.overlayBgColor
			: '#000000';

		this.overlayOpacity = Type.isNumber(options.overlayOpacity)
			? Math.min(Math.max(options.overlayOpacity, 0), 100)
			: 40;

		this.overlayBgCallback = Type.isFunction(options.overlayBgCallback) ? options.overlayBgCallback : null;

		this.#startPosition = ['right', 'bottom', 'top'].includes(options.startPosition as string)
			? (options.startPosition as 'right' | 'bottom' | 'top')
			: this.#startPosition;

		this.#outerBoundary = Type.isPlainObject(options.outerBoundary) ? options.outerBoundary : {};
		this.#startAnimationState = this.#getAnimationState('start');
		this.#endAnimationState = this.#getAnimationState('end');
		this.#currentAnimationState = null;

		this.overlayAnimation = false;
		this.animationName = 'sliding';
		this.animationOptions = {};

		this.minimizeOptions = null;
		this.setMinimizeOptions(options.minimizeOptions as MinimizeOptions | null);
		this.setToolbarOnOpen(options.hideToolbarOnOpen);
		this.setDesignSystemContext(options.designSystemContext);
		this.setAutoOffset(options.autoOffset);

		this.label = new Label(this, {
			className: '--close-label --ui-hoverable',
			iconClass: 'side-panel-label-icon-close ui-icon-set --cross-l',
			iconTitle: Loc.getMessage('MAIN_SIDEPANEL_CLOSE') as string,
			testId: 'main-sidepanel-close',
			onclick(label, slider)
			{
				slider.close();
			},
		});

		const labelOptions = Type.isPlainObject(options.label) ? options.label : {};
		this.label.setText(labelOptions.text);
		this.label.setColor(labelOptions.color);
		this.label.setBgColor(labelOptions.bgColor, labelOptions.opacity);

		this.minimizeLabel = new Label(this, {
			className: '--ui-hoverable',
			iconClass: 'side-panel-label-icon-minimize ui-icon-set --o-minimize',
			iconTitle: Loc.getMessage('MAIN_SIDEPANEL_MINIMIZE') as string,
			testId: 'main-sidepanel-minimize',
			onclick: (label, slider) => {
				if (this.isLoaded())
				{
					this.minimize();
				}
			},
			visible: this.areMinimizeOptionsValid(this.minimizeOptions),
		});
		this.newWindowLabel = null;
		this.copyLinkLabel = null;
		this.printLabel = null;

		if (options.newWindowLabel === true && (this.canChangeHistory() || Type.isStringFilled(options.newWindowUrl)))
		{
			this.newWindowLabel = new Label(this, {
				className: '--ui-hoverable',
				iconClass: 'side-panel-label-icon-new-window ui-icon-set --go-to-l',
				iconTitle: Loc.getMessage('MAIN_SIDEPANEL_NEW_WINDOW') as string,
				testId: 'main-sidepanel-new-window',
				onclick(label, slider)
				{
					const newWindowUrl = Type.isStringFilled(options.newWindowUrl) ? options.newWindowUrl : slider.getUrl();
					Object.assign(document.createElement('a'), {
						target: '_blank',
						href: newWindowUrl,
					}).click();
				},
			});
		}

		if (options.copyLinkLabel === true && (this.canChangeHistory() || Type.isStringFilled(options.newWindowUrl)))
		{
			this.copyLinkLabel = new Label(this, {
				className: '--ui-hoverable',
				iconClass: 'side-panel-label-icon-copy-link ui-icon-set --o-link',
				iconTitle: Loc.getMessage('MAIN_SIDEPANEL_COPY_LINK') as string,
				testId: 'main-sidepanel-copy-link',
			});

			const clipboard: any = Reflection.getClass('BX.clipboard');
			clipboard?.bindCopyClick(this.copyLinkLabel.getContainer(), {
				popup: {
					offsetTop: -10,
					offsetLeft: 17,
				},
				text: () => {
					const link = document.createElement('a');
					link.href = Type.isStringFilled(options.newWindowUrl) ? options.newWindowUrl : this.getUrl();

					return link.href;
				},
			});
		}

		this.printLabel = new Label(this, {
			hidden: !this.isPrintable(),
			className: '--side-panel-label-print --ui-hoverable',
			iconClass: 'side-panel-label-icon-print ui-icon-set --o-printer',
			iconTitle: Loc.getMessage('MAIN_SIDEPANEL_PRINT') as string,
			testId: 'main-sidepanel-print',
			onclick: this.#handlePrintBtnClick,
		});

		if (Type.isStringFilled(options.targetContainer) || Type.isElementNode(options.targetContainer))
		{
			this.#targetContainer = options.targetContainer;
		}

		[options.events].flat().forEach((events: SliderEvents | undefined) => this.#subscribeEvents(events));
	}

	#subscribeEvents(events: SliderEvents | undefined): void
	{
		if (Type.isPlainObject(events))
		{
			for (const [eventName, fn] of Object.entries(events))
			{
				if (Type.isFunction(fn))
				{
					EventEmitter.subscribe(this, Slider.getEventFullName(eventName), fn, { compatMode: true });
				}
			}
		}
	}

	static getEventFullName(eventName: string): string
	{
		return `SidePanel.Slider:${eventName}`;
	}

	open(): boolean
	{
		if (this.isOpen())
		{
			return false;
		}

		if (!this.canOpen())
		{
			return false;
		}

		if (this.isDestroyed())
		{
			return false;
		}

		if (this.maximizing)
		{
			this.fireEvent('onMaximizeStart');
		}

		this.createLayout();

		Dom.removeClass(this.getOverlay(), '--closing');
		Dom.addClass(this.getOverlay(), '--opening');

		this.adjustLayout();

		this.#zIndexComponent!.getStack()!.bringToFront(this.getOverlay());

		this.opened = true;

		this.fireEvent('onOpenStart');

		if (this.isLoaded())
		{
			this.getFocusTrap().activate();
		}
		else
		{
			this.getFocusTrap().activate({ initialFocus: false });
			this.getFocusTrap().focusContainer({ preventScroll: true });
		}

		this.#animateOpening();

		return true;
	}

	close(immediately?: boolean, callback?: Function): boolean
	{
		if (!this.isOpen())
		{
			return false;
		}

		if (!this.canClose())
		{
			return false;
		}

		if (this.minimizing)
		{
			this.fireEvent('onMinimizeStart');
		}

		this.fireEvent('onCloseStart');

		this.opened = false;

		if (this.isDestroyed())
		{
			return false;
		}

		if (this.animation)
		{
			this.animation.stop();
		}

		Dom.removeClass(this.getOverlay(), '--opening');
		Dom.addClass(this.getOverlay(), '--closing');

		this.fireEvent('onClosing');

		if (immediately === true || Browser.isMobile())
		{
			this.#currentAnimationState = this.#startAnimationState;
			this.#completeAnimation(callback);
		}
		else
		{
			this.animation = new Easing({
				duration: this.animationDuration,
				start: this.#currentAnimationState as Record<string, number>,
				finish: this.#startAnimationState as Record<string, number>,
				step: (state) => {
					this.#currentAnimationState = state;
					this.#animateStep(state);
				},
				complete: () => {
					this.#completeAnimation(callback);
				},
			});

			// Chrome rendering bug
			Dom.style(this.getContainer(), 'opacity', 0.96);

			if (this.animationName === 'scale' && Type.isStringFilled(this.animationOptions.origin))
			{
				Dom.style(this.getContainer(), 'transform-origin', this.animationOptions.origin);
			}

			this.animation.animate();
		}

		return true;
	}

	minimize(immediately?: boolean, callback?: Function): boolean
	{
		this.minimizing = true;

		const success = this.close(immediately, callback);
		if (!success)
		{
			this.minimizing = false;
		}

		return success;
	}

	isMinimizing(): boolean
	{
		return this.minimizing;
	}

	maximize(): boolean
	{
		this.maximizing = true;
		const success = this.open();
		if (!success)
		{
			this.maximizing = false;
		}

		return success;
	}

	isMaximizing(): boolean
	{
		return this.maximizing;
	}

	setAnimation(type: string, options?: { origin?: string }): void
	{
		this.animationName = type === 'scale' ? type : 'sliding';
		this.animationOptions = Type.isPlainObject(options) ? options : {};
	}

	setMinimizeOptions(minimizeOptions: MinimizeOptions | null): void
	{
		const showMinimizeLabel = this.areMinimizeOptionsValid(minimizeOptions);

		this.minimizeOptions = minimizeOptions;
		this.minimizeLabel?.setVisible(showMinimizeLabel);
	}

	areMinimizeOptionsValid(minimizeOptions: MinimizeOptions | null | undefined): boolean
	{
		return (
			Type.isPlainObject(minimizeOptions)
			&& Type.isStringFilled(minimizeOptions.entityType)
			&& (Type.isStringFilled(minimizeOptions.entityId) || Type.isNumber(minimizeOptions.entityId))
			&& Type.isStringFilled(minimizeOptions.url)
		);
	}

	getMinimizeOptions(): MinimizeOptions | null
	{
		return this.minimizeOptions;
	}

	setToolbarOnOpen(flag?: boolean): void
	{
		if (Type.isBoolean(flag))
		{
			this.#hideToolbarOnOpen = flag;
		}
	}

	shouldHideToolbarOnOpen(): boolean
	{
		return this.#hideToolbarOnOpen;
	}

	#getAnimationState(mode: 'start' | 'end'): Record<string, number>
	{
		const states = {
			right: {
				start: {
					translateX: 100,
					translateY: 0,
					opacity: 0,
					scale: 0,
					progress: 0,
					intensity: 0,
				},
				end: {
					translateX: 0,
					translateY: 0,
					opacity: this.overlayOpacity,
					scale: 100,
					progress: 100,
					intensity: 255,
				},
			},
			bottom: {
				start: {
					translateX: 0,
					translateY: 100,
					opacity: 0,
					scale: 0,
					progress: 0,
					intensity: 0,
				},
				end: {
					translateX: 0,
					translateY: 0,
					opacity: this.overlayOpacity,
					scale: 100,
					progress: 100,
					intensity: 255,
				},
			},
			top: {
				start: {
					translateX: 0,
					translateY: -100,
					opacity: 0,
					scale: 0,
					progress: 0,
					intensity: 0,
				},
				end: {
					translateX: 0,
					translateY: 0,
					opacity: this.overlayOpacity,
					scale: 100,
					progress: 100,
					intensity: 255,
				},
			},
		};

		return states[this.#startPosition][mode];
	}

	getDesignSystemContext(): string
	{
		return this.#designSystemContext;
	}

	setDesignSystemContext(context?: string): void
	{
		if (Type.isString(context))
		{
			if (this.layout.container !== null)
			{
				Dom.removeClass(this.layout.container, this.#designSystemContext);
				Dom.addClass(this.layout.container, context);
			}

			this.#designSystemContext = context;
		}
	}

	getUrl(): string
	{
		return this.url;
	}

	setUrl(url: string): void
	{
		if (Type.isStringFilled(url))
		{
			this.url = url;
		}
	}

	focus(): void
	{
		this.getWindow().focus();

		// if (this.isSelfContained())
		// {
		// 	this.getContentContainer().setAttribute("tabindex", "0");
		// 	this.getContentContainer().focus();
		// }
	}

	isOpen(): boolean
	{
		return this.opened;
	}

	getStartPosition(): 'right' | 'bottom' | 'top'
	{
		return this.#startPosition;
	}

	/**
	 * @deprecated
	 */
	setZindex(zIndex: number): void
	{}

	/**
	 * @public
	 * @returns {number}
	 */
	getZindex(): number
	{
		return this.getZIndexComponent()!.getZIndex();
	}

	getZIndexComponent(): ZIndexComponent | null
	{
		return this.#zIndexComponent;
	}

	setOffset(offset: number | null): void
	{
		if (Type.isNumber(offset) || offset === null)
		{
			this.offset = offset;
		}
	}

	getOffset(): number | null
	{
		return this.offset;
	}

	setAutoOffset(autoOffset?: boolean): void
	{
		if (Type.isBoolean(autoOffset))
		{
			this.#autoOffset = autoOffset;
		}
	}

	shouldUseAutoOffset(): boolean
	{
		return this.#autoOffset;
	}

	setWidth(width: number): void
	{
		if (Type.isNumber(width))
		{
			this.width = width;
		}
	}

	getWidth(): number | null
	{
		return this.width;
	}

	setTitle(title?: string): void
	{
		if (Type.isStringFilled(title))
		{
			this.title = title;
		}
	}

	getTitle(): string | null
	{
		return this.title;
	}

	getData(): Dictionary
	{
		return this.data;
	}

	isSelfContained(): boolean
	{
		return this.contentCallback !== null;
	}

	isCrossOriginAllowed(): boolean
	{
		return this.allowCrossOrigin;
	}

	isPostMethod(): boolean
	{
		return this.requestMethod === 'post';
	}

	getRequestParams(): JsonObject
	{
		return this.requestParams;
	}

	/**
	 * @public
	 * @returns {string}
	 */
	getFrameId(): string
	{
		if (this.iframeId === null)
		{
			this.iframeId = `iframe_${Text.getRandom(10).toLowerCase()}`;
		}

		return this.iframeId;
	}

	getWindow(): Window
	{
		return this.iframe ? (this.iframe.contentWindow as Window) : window;
	}

	getFrameWindow(): Window | null
	{
		return this.iframe ? this.iframe.contentWindow : null;
	}

	isHidden(): boolean
	{
		return this.hidden;
	}

	isCacheable(): boolean
	{
		return this.cacheable;
	}

	isFocusable(): boolean
	{
		return this.autoFocus;
	}

	isPrintable(): boolean
	{
		return this.printable;
	}

	isDestroyed(): boolean
	{
		return this.destroyed;
	}

	isLoaded(): boolean
	{
		return this.loaded;
	}

	canChangeHistory(): boolean
	{
		if (this.allowCrossOrigin || /^\/bitrix\/(components|tools)\//i.test(this.getUrl()))
		{
			return false;
		}

		if (this.allowChangeHistory === null)
		{
			return !this.isSelfContained();
		}

		return this.allowChangeHistory;
	}

	canChangeTitle(): boolean
	{
		if (this.allowChangeTitle === null)
		{
			if (this.getTitle() !== null)
			{
				return true;
			}

			return this.canChangeHistory();
		}

		return this.allowChangeTitle;
	}

	setCacheable(cacheable: boolean = true): void
	{
		this.cacheable = cacheable !== false;
	}

	setAutoFocus(autoFocus: boolean = true): void
	{
		this.autoFocus = autoFocus !== false;
	}

	/**
	 * @public
	 * @param {boolean} printable
	 */
	setPrintable(printable: boolean = true): void
	{
		this.printable = printable !== false;
		if (this.printable)
		{
			this.showPrintBtn();
		}
		else
		{
			this.hidePrintBtn();
		}
	}

	getLoader(): string | HTMLElement
	{
		return this.loader;
	}

	showLoader(): void
	{
		if (!this.layout.loader)
		{
			this.createLoader(this.loader, this.skeleton);
		}

		Dom.style(this.layout.loader, { opacity: 1, display: 'block' });
	}

	closeLoader(): void
	{
		if (this.layout.loader)
		{
			Dom.style(this.layout.loader, { opacity: 0, display: 'none' });
		}
	}

	showCloseBtn(): void
	{
		this.getLabel().show();
	}

	hideCloseBtn(): void
	{
		this.getLabel().hide();
	}

	showOrLightenCloseBtn(): void
	{
		if (Type.isStringFilled(this.getLabel().getText()))
		{
			this.getLabel().showIcon();
		}
		else
		{
			this.getLabel().lightenIcon();
		}
	}

	hideOrDarkenCloseBtn(): void
	{
		if (Type.isStringFilled(this.getLabel().getText()))
		{
			this.getLabel().hideIcon();
		}
		else
		{
			this.getLabel().darkenIcon();
		}
	}

	showPrintBtn(): void
	{
		if (this.printLabel !== null)
		{
			this.printLabel.show();
		}
	}

	hidePrintBtn(): void
	{
		if (this.printLabel !== null)
		{
			this.printLabel.hide();
		}
	}

	showExtraLabels(): void
	{
		Dom.removeClass(this.getExtraLabelsContainer(), '--hidden');
	}

	hideExtraLabels(): void
	{
		Dom.addClass(this.getExtraLabelsContainer(), '--hidden');
	}

	setContentClass(className: string): void
	{
		if (Type.isStringFilled(className))
		{
			this.removeContentClass();
			this.contentClassName = className;
			Dom.addClass(this.getContentContainer(), className);
		}
	}

	removeContentClass(): void
	{
		if (this.contentClassName !== null)
		{
			Dom.removeClass(this.getContentContainer(), this.contentClassName);
			this.contentClassName = null;
		}
	}

	setContainerClass(className: string): void
	{
		if (Type.isStringFilled(className))
		{
			this.removeContainerClass();
			this.containerClassName = className;
			Dom.addClass(this.getContainer(), className);
		}
	}

	removeContainerClass(): void
	{
		if (this.containerClassName !== null)
		{
			Dom.removeClass(this.getContainer(), this.containerClassName);
			this.containerClassName = null;
		}
	}

	setOverlayClass(className: string): void
	{
		if (Type.isStringFilled(className))
		{
			this.removeOverlayClass();
			this.overlayClassName = className;
			Dom.addClass(this.getOverlay(), className);
		}
	}

	removeOverlayClass(): void
	{
		if (this.overlayClassName !== null)
		{
			Dom.removeClass(this.getOverlay(), this.overlayClassName);
			this.overlayClassName = null;
		}
	}

	applyHacks(): void
	{
		// You can override this method in a derived class
	}

	applyPostHacks(): void
	{
		// You can override this method in a derived class
	}

	resetHacks(): void
	{
		// You can override this method in a derived class
	}

	resetPostHacks(): void
	{
		// You can override this method in a derived class
	}

	getTopBoundary(): number
	{
		return 0;
	}

	/**
	 * @protected
	 */
	calculateLeftBoundary(): number
	{
		const customLeftBoundary = this.getCustomLeftBoundary();
		if (customLeftBoundary !== null)
		{
			return customLeftBoundary;
		}

		return this.getLeftBoundary();
	}

	getLeftBoundary(): number
	{
		const windowWidth = Browser.isMobile() ? window.innerWidth : document.documentElement.clientWidth;

		return windowWidth < 1160 ? this.getMinLeftBoundary() : 300;
	}

	getMinLeftBoundary(): number
	{
		return this.hideControls && this.getCustomLeftBoundary() !== null ? 0 : 65;
	}

	/**
	 * @internal
	 */
	getLeftBoundaryOffset(): number
	{
		const offset = this.getOffset() === null ? 0 : (this.getOffset() as number);

		return Math.max(this.calculateLeftBoundary(), this.getMinLeftBoundary()) + offset;
	}

	setCustomLeftBoundary(boundary?: number | null): void
	{
		if (Type.isNumber(boundary) || boundary === null)
		{
			this.customLeftBoundary = boundary;
		}
	}

	getCustomLeftBoundary(): number | null
	{
		return this.customLeftBoundary;
	}

	setCustomRightBoundary(boundary?: number | null): void
	{
		if (Type.isNumber(boundary) || boundary === null)
		{
			this.customRightBoundary = boundary;
		}
	}

	getCustomRightBoundary(): number | null
	{
		return this.customRightBoundary;
	}

	/**
	 * @protected
	 */
	calculateRightBoundary(): number
	{
		const customRightBoundary = this.getCustomRightBoundary();
		if (customRightBoundary !== null)
		{
			return -window.pageXOffset + customRightBoundary;
		}

		return this.getRightBoundary();
	}

	getRightBoundary(): number
	{
		return -window.pageXOffset;
	}

	getOuterBoundary(): OuterBoundary
	{
		return this.#outerBoundary;
	}

	calculateOuterBoundary(): OuterBoundary | undefined
	{
		// You can override this method in a derived class
		return undefined;
	}

	#calculateOuterBoundary(): OuterBoundary
	{
		const outerBoundary = this.calculateOuterBoundary();

		return Runtime.merge(Type.isPlainObject(outerBoundary) ? outerBoundary : {}, this.getOuterBoundary());
	}

	destroy(): boolean
	{
		if (this.isDestroyed())
		{
			return false;
		}

		this.firePageEvent('onDestroy');
		this.fireFrameEvent('onDestroy');

		const frameWindow = this.getFrameWindow();
		if (frameWindow && !this.allowCrossOrigin)
		{
			Event.unbind(frameWindow, 'keydown', this.#handleFrameKeyDown);
			Event.unbind(frameWindow, 'focus', this.#handleFrameFocus);
			Event.unbind(frameWindow, 'unload', this.#handleFrameUnload);
		}
		else if (this.allowCrossOrigin)
		{
			Event.unbind(window, 'message', this.#handleCrossOriginWindowMessage);
		}

		EventEmitter.unsubscribe('BX.Main.Popup:onInit', this.#handlePopupInit);
		ZIndexManager.unregister(this.layout.overlay as HTMLElement);
		this.#zIndexComponent = null;

		FocusMonitor.Instance.detachIframe(this.getFrame());

		if (this.#focusTrap !== null)
		{
			this.#focusTrap.destroy();
		}

		this.#focusTrap = null;

		Dom.remove(this.layout.overlay);

		this.layout.container = null;
		this.layout.overlay = null;
		this.layout.content = null;
		this.layout.closeBtn = null;
		this.layout.loader = null;

		this.#refs = new MemoryCache<HTMLElement>();

		this.iframe = null;
		this.destroyed = true;

		EventEmitter.unsubscribeAll(this);

		this.firePageEvent('onDestroyComplete');

		return true;
	}

	/**
	 * @internal
	 */
	hide(): void
	{
		this.hidden = true;

		Dom.style(this.getContainer(), 'display', 'none');
		Dom.style(this.getOverlay(), 'display', 'none');
	}

	/**
	 * @internal
	 */
	unhide(): void
	{
		this.hidden = false;

		Dom.style(this.getContainer(), 'display', null);
		Dom.style(this.getOverlay(), 'display', null);
	}

	/**
	 * @public
	 */
	reload(): void
	{
		this.loaded = false;
		if (this.isSelfContained())
		{
			this.contentCallbackInvoved = false;
			this.showLoader();
			this.setContent();
		}
		else
		{
			this.showLoader();
			this.getFrameWindow()!.location.reload();
		}
	}

	/**
	 * @public
	 */
	adjustLayout(): void
	{
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const windowHeight = Browser.isMobile() ? window.innerHeight : document.documentElement.clientHeight;

		let topBoundary = this.getTopBoundary();
		const isTopBoundaryVisible = topBoundary - scrollTop > 0;
		topBoundary = isTopBoundaryVisible ? topBoundary : scrollTop;

		const height = isTopBoundaryVisible ? windowHeight - topBoundary + scrollTop : windowHeight;
		const leftBoundary = this.getLeftBoundaryOffset();
		const rightBoundary = this.calculateRightBoundary();

		Dom.style(this.getOverlay(), {
			left: `${window.pageXOffset}px`,
			top: `${topBoundary}px`,
			right: `${rightBoundary}px`,
			height: `${height}px`,
		});

		const { right = null, top = null, bottom = null } = this.#calculateOuterBoundary();

		Dom.style(this.getContainer(), {
			width: `calc(100% - ${leftBoundary + (right === null ? 0 : right)}px)`,
			maxWidth: this.getWidth() === null ? null : `${this.getWidth()}px`,
			right: right === null ? null : `${right}px`,
			top: top === null ? null : `${top}px`,
			bottom: bottom === null ? null : `${bottom}px`,
			// height: `${height}px`, // height: '100%',
		} as unknown as Record<string, string | number>);

		this.getLabel().adjustLayout();

		this.fireEvent('onLayout');
	}

	private createLayout()
	{
		if (this.layout.overlay !== null && this.layout.overlay.parentNode)
		{
			return;
		}

		this.getContainer().ariaModal = this.getFocusTrap().isLooped() as unknown as string;
		if (this.getTitle() !== null)
		{
			this.getContainer().ariaLabel = this.getTitle();
		}

		if (this.isSelfContained())
		{
			Dom.addClass(this.getOverlay(), '--self-contained');
			Dom.append(this.getOverlay(), this.getTargetContainer());

			this.setContent();

			EventEmitter.subscribe('BX.Main.Popup:onInit', this.#handlePopupInit);
		}
		else
		{
			Dom.append(this.getFrame(), this.getContentContainer());
			Dom.append(this.getOverlay(), this.getTargetContainer());
			this.setFrameSrc(); // setFrameSrc must be below than appendChild, otherwise POST method fails.
		}

		const stack = ZIndexManager.getOrAddStack(document.body);
		this.#zIndexComponent = stack!.register(this.getOverlay());
	}

	getTargetContainer(): HTMLElement
	{
		if (this.#targetContainer === null)
		{
			return document.body;
		}

		if (Type.isElementNode(this.#targetContainer))
		{
			return this.#targetContainer;
		}

		const container = document.querySelector(this.#targetContainer);
		if (Type.isElementNode(container))
		{
			return container;
		}

		return document.body;
	}

	getFrame(): HTMLIFrameElement
	{
		if (this.iframe !== null)
		{
			return this.iframe;
		}

		this.iframe = Dom.create('iframe', {
			attrs: {
				referrerpolicy: (this.allowCrossOrigin ? 'strict-origin' : false) as unknown as string,
				src: 'about:blank',
				frameborder: '0',
				'data-testid': 'main-sidepanel-iframe',
			},
			props: {
				className: 'side-panel-iframe',
				name: this.getFrameId(),
				id: this.getFrameId(),
			},
			events: {
				load: this.handleFrameLoad.bind(this),
			},
		}) as HTMLIFrameElement;

		return this.iframe;
	}

	getOverlay(): HTMLElement
	{
		if (this.layout.overlay !== null)
		{
			return this.layout.overlay;
		}

		const overlayClass = this.overlayClassName === null ? '' : ` ${this.overlayClassName}`;

		this.layout.overlay = Dom.create('div', {
			props: {
				className: `side-panel side-panel-overlay${overlayClass}`,
			},
			attrs: {
				'data-testid': 'main-sidepanel-overlay',
			},
			events: {
				mousedown: this.#handleOverlayClick as EventListener,
			},
			children: [this.getContainer()],
		});

		return this.layout.overlay;
	}

	unhideOverlay(): void
	{
		Dom.removeClass(this.getOverlay(), '--hidden');
	}

	hideOverlay(): void
	{
		Dom.addClass(this.getOverlay(), '--hidden');
	}

	hideShadow(): void
	{
		Dom.removeClass(this.getContainer(), 'side-panel-show-shadow');
	}

	showShadow(): void
	{
		Dom.addClass(this.getContainer(), 'side-panel-show-shadow');
	}

	setOverlayBackground(): void
	{
		if (this.overlayBgCallback === null)
		{
			const opacity = parseInt(((this.overlayOpacity / 100) * 255) as unknown as string, 10)
				.toString(16)
				.padStart(2, 0 as unknown as string);
			Dom.style(this.getOverlay(), 'background-color', `${this.overlayBgColor}${opacity}`);
		}
		else
		{
			const state = this.#getAnimationState('end');
			Dom.style(this.getOverlay(), 'background', this.overlayBgCallback(state, this));
		}
	}

	setOverlayAnimation(animate: boolean): void
	{
		if (Type.isBoolean(animate))
		{
			this.overlayAnimation = animate;
		}
	}

	getOverlayAnimation(): boolean
	{
		return this.overlayAnimation;
	}

	getOverlayBgColor(): string
	{
		return this.overlayBgColor;
	}

	getOverlayOpacity(): number
	{
		return this.overlayOpacity;
	}

	getContainer(): HTMLElement
	{
		if (this.layout.container !== null)
		{
			return this.layout.container;
		}

		const content = Tag.render`
			<div class="side-panel-content-wrapper">${this.getContentContainer()}</div>
		`;

		this.layout.container = Tag.render`
			<div class="side-panel side-panel-container" role="dialog" aria-busy="true" data-testid="main-sidepanel-container">
				${this.hideControls ? content : [content, this.getLabelsContainer()]}
			</div>
		`;

		Dom.addClass(this.layout.container, this.getDesignSystemContext());
		Dom.addClass(this.layout.container, this.containerClassName as string);

		return this.layout.container as HTMLElement;
	}

	getContentContainer(): HTMLElement
	{
		if (this.layout.content !== null)
		{
			return this.layout.content;
		}

		const contentClass = this.contentClassName === null ? '' : ` ${this.contentClassName}`;

		this.layout.content = Dom.create('div', {
			props: {
				className: `side-panel-content-container${contentClass}`,
			},
			attrs: {
				'data-testid': 'main-sidepanel-content',
			},
		});

		return this.layout.content;
	}

	getLabelsContainer(): HTMLElement
	{
		return this.#refs.remember('labels-container', () => {
			return Dom.create('div', {
				props: {
					className: 'side-panel-labels',
				},
				children: [this.getLabel().getContainer(), this.getExtraLabelsContainer()],
			});
		});
	}

	getExtraLabelsContainer(): HTMLElement
	{
		return this.#refs.remember('icon-labels', () => {
			return Dom.create('div', {
				props: {
					className: 'side-panel-extra-labels',
				},
				children: [
					this.copyLinkLabel ? this.copyLinkLabel.getContainer() : null,
					this.minimizeLabel.getContainer(),
					this.newWindowLabel ? this.newWindowLabel.getContainer() : null,
					this.printLabel ? this.printLabel.getContainer() : null,
				] as unknown as Array<Node | string>,
			});
		});
	}

	getCloseBtn(): HTMLElement
	{
		return this.getLabel().getIconBox();
	}

	getLabel(): Label
	{
		return this.label;
	}

	getNewWindowLabel(): Label | null
	{
		return this.newWindowLabel;
	}

	getCopyLinkLabel(): Label | null
	{
		return this.copyLinkLabel;
	}

	getMinimizeLabel(): Label
	{
		return this.minimizeLabel;
	}

	getPrintLabel(): Label | null
	{
		return this.printLabel;
	}

	private setContent(): void
	{
		if (this.contentCallbackInvoved)
		{
			return;
		}

		this.contentCallbackInvoved = true;

		Dom.clean(this.getContentContainer());

		let promise: any = this.contentCallback!(this);
		const isPromiseReturned = promise
			&& (Object.prototype.toString.call(promise) === '[object Promise]' || promise.toString() === '[object BX.Promise]');

		if (!isPromiseReturned)
		{
			promise = Promise.resolve(promise);
		}

		promise
			.then((result: any) => {
				if (this.isDestroyed())
				{
					return;
				}

				const onLoad = () => {
					this.getContainer().ariaLabel = this.getTitle() || '';
					this.getContainer().ariaBusy = false as unknown as string;
					this.getFocusTrap().applyInitialFocus();
					this.removeLoader();
					this.loaded = true;
					this.firePageEvent('onLoad');
				};

				if (Type.isPlainObject(result) && Type.isStringFilled(result.html))
				{
					(Runtime.html(this.getContentContainer(), result.html) as Promise<any>)
						.then(onLoad)
						.catch((reason: any) => {
							this.removeLoader();
							this.getContentContainer().innerHTML = reason;
						});
				}
				else
				{
					if (Type.isDomNode(result))
					{
						Dom.append(result as HTMLElement, this.getContentContainer());
					}
					else if (Type.isStringFilled(result))
					{
						this.getContentContainer().innerHTML = result;
					}

					onLoad();
				}
			})
			.catch((reason: any) => {
				this.removeLoader();
				this.getContentContainer().innerHTML = reason;
			});
	}

	private setFrameSrc(): void
	{
		if (this.iframeSrc === this.getUrl())
		{
			return;
		}

		const url = Uri.addParam(this.getUrl(), { IFRAME: 'Y', IFRAME_TYPE: 'SIDE_SLIDER' });
		if (this.isPostMethod())
		{
			const form = document.createElement('form');
			form.method = 'POST';
			form.action = url;
			form.target = this.getFrameId();
			Dom.style(form, 'display', 'none');

			BX.util.addObjectToForm(this.getRequestParams(), form);

			Dom.append(form, document.body);

			form.submit();

			Dom.remove(form);
		}
		else
		{
			this.iframeSrc = this.getUrl();
			this.iframe!.src = url;
		}

		this.loaded = false;
		this.#listenIframeLoading();
	}

	private createLoader(sliderLoader: string | HTMLElement, skeleton: string | null | undefined): void
	{
		Dom.remove(this.layout.loader);

		const loader = Type.isStringFilled(sliderLoader) || Type.isElementNode(sliderLoader)
			? sliderLoader
			: 'default-loader';

		const oldLoaders = [
			'task-new-loader',
			'task-edit-loader',
			'task-view-loader',
			'crm-entity-details-loader',
			'crm-button-view-loader',
			'crm-webform-view-loader',
			'create-mail-loader',
			'view-mail-loader',
		];

		if (Type.isStringFilled(skeleton))
		{
			this.layout.loader = Tag.render`<div style="height: 100%;"></div>`;

			void renderSkeleton(skeleton, this.layout.loader as HTMLElement);
		}
		else if (Type.isElementNode(loader))
		{
			this.layout.loader = this.createHTMLLoader(loader);
		}
		else if (oldLoaders.includes(loader) && this.loaderExists(loader))
		{
			this.layout.loader = this.createOldLoader(loader);
		}
		else if (loader.charAt(0) === '/')
		{
			this.layout.loader = this.createSvgLoader(loader);
		}
		else
		{
			const matches = loader.match(/^([\w.-]+):([\w.-]+)$/i);
			if (matches)
			{
				const moduleId = matches[1];
				const svgName = matches[2];
				const svg = `/bitrix/images/${moduleId}/slider/${svgName}.svg`;
				this.layout.loader = this.createSvgLoader(svg);
			}
			else
			{
				this.layout.loader = this.createDefaultLoader();
			}
		}

		Dom.append(this.layout.loader, this.getContainer());
	}

	createSvgLoader(svg: string): HTMLElement
	{
		return Dom.create('div', {
			props: {
				className: 'side-panel-loader',
			},
			children: [
				Dom.create('div', {
					props: {
						className: 'side-panel-loader-container',
					},
					style: {
						backgroundImage: `url("${svg}")`,
					},
				}),
			],
		});
	}

	createDefaultLoader(): HTMLElement
	{
		return Dom.create('div', {
			props: {
				className: 'side-panel-loader',
			},
			children: [
				Dom.create('div', {
					props: {
						className: 'side-panel-default-loader-container',
					},
					html:
						'<svg class="side-panel-default-loader-circular" viewBox="25 25 50 50">'
						+ '<circle '
						+ 'class="side-panel-default-loader-path" '
						+ 'cx="50" cy="50" r="20" fill="none" stroke-miterlimit="10"'
						+ '/>'
						+ '</svg>',
				}),
			],
		});
	}

	private createOldLoader(loader: string): HTMLElement
	{
		if (loader === 'crm-entity-details-loader')
		{
			return Dom.create('div', {
				props: {
					className: `side-panel-loader ${loader}`,
				},
				children: [
					Dom.create('img', {
						attrs: {
							src:
								'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAMAAABhq6zVAAAAA1BMVEX'
								+ '///+nxBvIAAAAAXRSTlMAQObYZgAAAAtJREFUeAFjGMQAAACcAAG25ruvAAAAAElFTkSuQmCC',
						},
						props: {
							className: 'side-panel-loader-mask top',
						},
					}),
					Dom.create('div', {
						props: {
							className: 'side-panel-loader-bg left',
						},
						children: [
							Dom.create('img', {
								attrs: {
									src:
										'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAMAAABhq6zVAAAAA1B'
										+ 'MVEX///+nxBvIAAAAAXRSTlMAQObYZgAAAAtJREFUeAFjGMQAAACcAAG25ruvAAAAAElFTkSuQmCC',
								},
								props: {
									className: 'side-panel-loader-mask left',
								},
							}),
						],
					}),
					Dom.create('div', {
						props: {
							className: 'side-panel-loader-bg right',
						},
						children: [
							Dom.create('img', {
								attrs: {
									src:
										'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAMAAABhq6zVAAAAA1BM'
										+ 'VEX///+nxBvIAAAAAXRSTlMAQObYZgAAAAtJREFUeAFjGMQAAACcAAG25ruvAAAAAElFTkSuQmCC',
								},
								props: {
									className: 'side-panel-loader-mask right',
								},
							}),
						],
					}),
				],
			});
		}

		return Dom.create('div', {
			props: {
				className: `side-panel-loader ${loader}`,
			},
			children: [
				Dom.create('img', {
					attrs: {
						src:
							'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAMAAABhq6zVAAAAA1BMVEX'
							+ '///+nxBvIAAAAAXRSTlMAQObYZgAAAAtJREFUeAFjGMQAAACcAAG25ruvAAAAAElFTkSuQmCC',
					},
					props: {
						className: 'side-panel-loader-mask left',
					},
				}),
				Dom.create('img', {
					attrs: {
						src:
							'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAMAAABhq6zVAAAAA'
							+ '1BMVEX///+nxBvIAAAAAXRSTlMAQObYZgAAAAtJREFUeAFjGMQAAACcAAG25ruvAAAAAElFTkSuQmCC',
					},
					props: {
						className: 'side-panel-loader-mask right',
					},
				}),
			],
		});
	}

	private createHTMLLoader(loader: HTMLElement): HTMLElement
	{
		return Dom.create('div', {
			children: [loader],
		});
	}

	loaderExists(loader: string): boolean
	{
		if (!Type.isStringFilled(loader))
		{
			return false;
		}

		for (let i = 0; i < document.styleSheets.length; i++)
		{
			const style = document.styleSheets[i];
			if (!Type.isStringFilled(style.href) || !style.href.includes('sidepanel'))
			{
				continue;
			}

			let rules = null;
			try
			{
				rules = style.rules || style.cssRules;
			}
			catch
			{
				try
				{
					rules = style.cssRules;
				}
				catch
				{
					rules = [];
				}
			}

			for (const rule of rules)
			{
				if (Type.isStringFilled(rule.selectorText) && rule.selectorText.includes(loader))
				{
					return true;
				}
			}
		}

		return false;
	}

	private removeLoader()
	{
		Dom.remove(this.layout.loader);
		this.layout.loader = null;
	}

	getFocusTrap(): FocusTrap
	{
		if (this.#focusTrap === null)
		{
			const defaultFocusTrapOptions: FocusTrapOptions = {
				isolateOutside: true,
				initialFocus: ['[data-autofocus]', 'container'],
			};

			const focusTrapOptions: FocusTrapOptions = (
				Type.isPlainObject(this.#options.focusTrap)
					? (this.#options.focusTrap as FocusTrapOptions)
					: {}
			);

			this.#focusTrap = new FocusTrap(
				this.getContainer(),
				Runtime.merge(defaultFocusTrapOptions, focusTrapOptions),
			);
		}

		return this.#focusTrap;
	}

	#animateOpening()
	{
		if (this.animation)
		{
			this.animation.stop();
		}

		this.fireEvent('onOpening');

		if (Browser.isMobile())
		{
			this.#currentAnimationState = this.#endAnimationState;
			this.#animateStep(this.#currentAnimationState as Record<string, number>);
			this.#completeAnimation();

			return;
		}

		this.#currentAnimationState = this.#currentAnimationState === null
			? this.#startAnimationState
			: this.#currentAnimationState;

		if (this.skeleton)
		{
			this.showLoader();
		}

		this.animation = new Easing({
			duration: this.animationDuration,
			start: this.#currentAnimationState as Record<string, number>,
			finish: this.#endAnimationState as Record<string, number>,
			step: (state) => {
				this.#currentAnimationState = state;
				this.#animateStep(state);
			},
			complete: () => {
				this.#completeAnimation();
			},
		});

		if (this.animationName === 'scale' && Type.isStringFilled(this.animationOptions.origin))
		{
			Dom.style(this.getContainer(), 'transform-origin', this.animationOptions.origin);
		}

		this.animation.animate();
	}

	#animateStep(state: Record<string, number>)
	{
		if (this.animationName === 'scale')
		{
			Dom.style(this.getContainer(), 'transform', `scale(${state.scale / 100})`);
		}
		else
		{
			Dom.style(this.getContainer(), 'transform', `translate(${state.translateX}%, ${state.translateY}%)`);
		}

		if (this.getOverlayAnimation())
		{
			if (this.overlayBgCallback === null)
			{
				const opacity = parseInt(((state.opacity / 100) * 255) as unknown as string, 10)
					.toString(16)
					.padStart(2, 0 as unknown as string);
				Dom.style(this.getOverlay(), 'background-color', `${this.overlayBgColor}${opacity}`);
			}
			else
			{
				Dom.style(this.getOverlay(), 'background', this.overlayBgCallback(state, this));
			}
		}
	}

	#completeAnimation(callback?: Function)
	{
		this.animation = null;
		if (this.isOpen())
		{
			this.#currentAnimationState = this.#endAnimationState;
			this.maximizing = false;

			Dom.removeClass(this.getOverlay(), '--opening');
			Dom.addClass(this.getOverlay(), '--open');
			if (this.animationName === 'scale')
			{
				const state = this.#getAnimationState('end');
				Dom.style(this.getContainer(), {
					'transform-origin': null,
					transform: `translate(${state.translateX}%, ${state.translateY}%)`,
				} as unknown as Record<string, string | number>);
			}

			this.firePageEvent('onBeforeOpenComplete');
			this.fireFrameEvent('onBeforeOpenComplete');

			this.firePageEvent('onOpenComplete');
			this.fireFrameEvent('onOpenComplete');

			if (!this.isLoaded())
			{
				this.showLoader();
			}
		}
		else
		{
			this.#currentAnimationState = this.#startAnimationState;
			this.minimizing = false;

			Dom.removeClass(this.getOverlay(), '--open --opening --closing');
			if (this.animationName === 'scale')
			{
				const state = this.#getAnimationState('start');
				Dom.style(this.getContainer(), {
					'transform-origin': null,
					transform: `translate(${state.translateX}%, ${state.translateY}%)`,
				} as unknown as Record<string, string | number>);
			}

			Dom.style(this.getContainer(), {
				width: null,
				right: null,
				opacity: null,
				'max-width': null,
				'min-width': null,
			} as unknown as Record<string, string | number>);

			Dom.style(this.getCloseBtn(), 'opacity', null);

			this.firePageEvent('onBeforeCloseComplete');
			this.fireFrameEvent('onBeforeCloseComplete');

			this.firePageEvent('onCloseComplete');
			this.fireFrameEvent('onCloseComplete');

			if (Type.isFunction(callback))
			{
				callback(this);
			}

			if (!this.isDestroyed())
			{
				this.getFocusTrap().deactivate();
			}

			if (!this.isCacheable())
			{
				this.destroy();
			}
		}
	}

	/**
	 * @internal
	 */
	firePageEvent(eventName: string | SliderEvent): SliderEvent
	{
		const sliderEvent = this.getEvent(eventName);
		if (sliderEvent === null)
		{
			throw new Error("'eventName' is invalid.");
		}

		EventEmitter.emit(
			this,
			sliderEvent.getFullName().toLowerCase(),
			new BaseEvent({ data: [sliderEvent], compatData: [sliderEvent] }),
		);

		// Events for compatibility
		const compatName = Type.isString(eventName) ? eventName : null;
		if (compatName !== null && ['onClose', 'onOpen'].includes(compatName))
		{
			EventEmitter.emit(`BX.Bitrix24.PageSlider:${compatName}`, new BaseEvent({ data: [this], compatData: [this] }));
			EventEmitter.emit(`Bitrix24.Slider:${compatName}`, new BaseEvent({ data: [this], compatData: [this] }));
		}

		return sliderEvent;
	}

	/**
	 * @internal
	 */
	fireFrameEvent(eventName: string | SliderEvent): SliderEvent | null
	{
		const sliderEvent = this.getEvent(eventName);
		if (sliderEvent === null)
		{
			throw new Error("'eventName' is invalid.");
		}

		if (this.allowCrossOrigin)
		{
			return null;
		}

		const frameWindow = this.getFrameWindow() as any;
		if (frameWindow && frameWindow.BX && frameWindow.BX.onCustomEvent)
		{
			frameWindow.BX.onCustomEvent(this, sliderEvent.getFullName(), [sliderEvent]);

			// Events for compatibility
			const compatName = Type.isString(eventName) ? eventName : null;
			if (compatName !== null && ['onClose', 'onOpen'].includes(compatName))
			{
				frameWindow.BX.onCustomEvent(`BX.Bitrix24.PageSlider:${compatName}`, [this]);
				frameWindow.BX.onCustomEvent(`Bitrix24.Slider:${compatName}`, [this]); // Compatibility
			}
		}

		return sliderEvent;
	}

	fireEvent(eventName: string): void
	{
		this.firePageEvent(eventName);
		this.fireFrameEvent(eventName);
	}

	private getEvent(eventName: string | SliderEvent): SliderEvent | null
	{
		let event = null;
		if (Type.isStringFilled(eventName))
		{
			event = new SliderEvent();
			event.setSlider(this);
			event.setName(eventName);
		}
		else if (eventName instanceof SliderEvent)
		{
			event = eventName;
		}

		return event;
	}

	canOpen(): boolean
	{
		return this.canAction('open');
	}

	canClose(): boolean
	{
		return this.canAction('close');
	}

	canCloseByEsc(): boolean
	{
		return this.canAction('closeByEsc');
	}

	canAction(action: string): boolean
	{
		if (!Type.isStringFilled(action))
		{
			return false;
		}

		const eventName = `on${action.charAt(0).toUpperCase()}${action.slice(1)}`;

		const pageEvent = this.firePageEvent(eventName);
		const frameEvent = this.fireFrameEvent(eventName);

		return pageEvent.isActionAllowed() && (!frameEvent || frameEvent.isActionAllowed());
	}

	#handleCrossOriginWindowMessage = (event: { origin: string; data: any }) => {
		const frameUrl = new URL(this.url);
		const eventUrl = new URL(event.origin);
		if (eventUrl.origin !== frameUrl.origin)
		{
			return;
		}

		const message: { type: string; data: any } = { type: '', data: undefined };
		if (Type.isString(event.data))
		{
			message.type = event.data;
		}
		else if (Type.isPlainObject(event.data))
		{
			message.type = event.data.type as string;
			message.data = event.data.data;
		}

		switch (message.type)
		{
			case 'BX:SidePanel:close':
			{
				this.close();

				break;
			}

			case 'BX:SidePanel:load:force':
			{
				if (!this.isLoaded() && !this.isDestroyed())
				{
					this.handleFrameLoad();
				}

				break;
			}

			case 'BX:SidePanel:data:send':
			{
				const pageEvent = new MessageEvent({ sender: this, data: message.data });
				pageEvent.setName('onXDomainMessage');
				this.firePageEvent(pageEvent);

				break;
			}

			default:
			// No default
		}
	};

	private handleFrameLoad(event?: Event)
	{
		if (this.loaded)
		{
			return;
		}

		const frameWindow = this.iframe!.contentWindow as Window;
		const iframeLocation = frameWindow.location;

		if (this.allowCrossOrigin)
		{
			Event.bind(window, 'message', this.#handleCrossOriginWindowMessage);
		}

		try
		{
			if (iframeLocation.toString() === 'about:blank')
			{
				return;
			}
		}
		catch (e)
		{
			if (this.allowCrossOrigin)
			{
				this.loaded = true;
				this.closeLoader();

				return;
			}

			// eslint-disable-next-line no-console
			console.warn('SidePanel: Try to use "allowCrossOrigin: true" option.');

			throw e;
		}

		Event.bind(frameWindow, 'keydown', this.#handleFrameKeyDown);
		Event.bind(frameWindow, 'focus', this.#handleFrameFocus);
		Event.bind(frameWindow, 'unload', this.#handleFrameUnload);

		if (Browser.isMobile())
		{
			frameWindow.document.body.style.paddingBottom = `${(window.innerHeight * 2) / 3}px`;
		}

		const iframeUrl = iframeLocation.pathname + iframeLocation.search + iframeLocation.hash;
		this.iframeSrc = this.refineUrl(iframeUrl);
		this.url = this.iframeSrc;

		if (this.isPrintable())
		{
			this.#injectPrintStyles();
		}

		this.loaded = true;
		this.loadedCnt++;

		FocusMonitor.Instance.detachIframe(this.getFrame());
		FocusMonitor.Instance.attachIframe(this.getFrame());

		if (this.getTitle() === null)
		{
			// fallback
			const title = this.getFrameWindow() ? this.getFrameWindow()?.document?.title : null;
			if (Type.isStringFilled(title))
			{
				this.getContainer().ariaLabel = Type.isStringFilled(title) ? title : '';
			}
		}

		this.getContainer().ariaBusy = 'false';
		this.getFocusTrap().applyInitialFocus();

		if (this.loadedCnt > 1)
		{
			this.firePageEvent('onLoad');
			this.fireFrameEvent('onLoad');

			this.firePageEvent('onReload');
			this.fireFrameEvent('onReload');
		}
		else
		{
			this.firePageEvent('onLoad');
			this.fireFrameEvent('onLoad');
		}

		this.closeLoader();
	}

	#listenIframeLoading()
	{
		if (this.allowCrossOrigin)
		{
			return;
		}

		const isLoaded = setInterval(() => {
			if (this.isLoaded() || this.isDestroyed())
			{
				clearInterval(isLoaded);

				return;
			}

			if (this.iframe!.contentWindow!.location.toString() === 'about:blank')
			{
				return;
			}

			if (
				this.iframe!.contentWindow!.document.readyState === 'complete'
				|| this.iframe!.contentWindow!.document.readyState === 'interactive'
			)
			{
				clearInterval(isLoaded);
				this.handleFrameLoad();
			}
		}, 200);
	}

	#handleFrameUnload = () => {
		this.loaded = false;
		this.#listenIframeLoading();
	};

	#handleFrameKeyDown = (event: KeyboardEvent) => {
		if (event.keyCode !== 27)
		{
			return;
		}

		const framePopupManager = (this.getWindow() as any).BX?.Main?.PopupManager;
		if (framePopupManager)
		{
			const popups = framePopupManager.getPopups();
			for (const popup of popups)
			{
				if (popup.isShown())
				{
					return;
				}
			}
		}

		const centerX = this.getWindow().document.documentElement.clientWidth / 2;
		const centerY = this.getWindow().document.documentElement.clientHeight / 2;
		const element = this.getWindow().document.elementFromPoint(centerX, centerY);

		if (Dom.hasClass(element, 'bx-core-dialog-overlay') || Dom.hasClass(element, 'bx-core-window'))
		{
			return;
		}

		if (element!.closest('.bx-core-window'))
		{
			return;
		}

		this.firePageEvent('onEscapePress');
		this.fireFrameEvent('onEscapePress');
	};

	isOnTopOfPopup(popup: Popup): boolean
	{
		const sameStack = this.getZIndexComponent()!.getStack() === popup.getZIndexComponent().getStack();
		const popupOnTop = sameStack && popup.getZindex() > this.getZindex();
		let popupInside = this.getContainer().contains(popup.getPopupContainer());
		if (this.getFrameWindow() && !this.allowCrossOrigin)
		{
			popupInside = this.getFrameWindow()!.document.contains(popup.getPopupContainer());
		}

		return !(popup.isShown() && (popupOnTop || popupInside));
	}

	#handlePopupInit = (event: BaseEvent) => {
		const data = event.getCompatData();
		const bindElement = data![1];
		const params = data![2];

		if (
			!Type.isElementNode(params.targetContainer)
			&& Type.isElementNode(bindElement)
			&& this.getContentContainer().contains(bindElement)
		)
		{
			params.targetContainer = this.getContentContainer();
		}
	};

	#handleFrameFocus = (event?: Event) => {
		this.firePageEvent('onFrameFocus');
	};

	#handleOverlayClick = (event: MouseEvent) => {
		if (event.target === this.getOverlay())
		{
			if (this.animation === null)
			{
				this.close();
				event.stopPropagation();
			}
			else
			{
				event.preventDefault();
			}
		}
	};

	#handlePrintBtnClick = () => {
		if (this.isSelfContained())
		{
			const frame = document.createElement('iframe');
			frame.src = 'about:blank';
			frame.name = 'sidepanel-print-frame';

			Dom.style(frame, 'display', 'none');
			Dom.append(frame, document.body);

			const frameWindow = frame.contentWindow as Window;
			const frameDoc = frameWindow.document;
			frameDoc.open();
			frameDoc.write('<html><head>');

			let headTags = '';
			const links = document.head.querySelectorAll('link, style');
			for (const link of links)
			{
				headTags += link.outerHTML;
			}

			headTags += '<style>html, body { background: #fff !important; height: 100%; }</style>';

			frameDoc.write(headTags);

			frameDoc.write('</head><body>');
			frameDoc.write(this.getContentContainer().innerHTML);
			frameDoc.write('</body></html>');
			frameDoc.close();

			frameWindow.focus();
			frameWindow.print();

			setTimeout(() => {
				Dom.remove(frame);
				window.focus();
			}, 1000);
		}
		else
		{
			this.focus();
			this.getFrameWindow()!.print();
		}
	};

	#injectPrintStyles()
	{
		const frameDocument = this.getFrameWindow()!.document;

		let bodyClass = '';

		const classList = frameDocument.body.classList;
		for (const className of classList)
		{
			bodyClass += `.${className}`;
		}

		const bodyStyle = `@media print { body${bodyClass} { `
			+ 'background: #fff !important; '
			+ '-webkit-print-color-adjust: exact;'
			+ 'color-adjust: exact; '
			+ '} }';

		const style = frameDocument.createElement('style');
		style.type = 'text/css';
		if ((style as any).styleSheet)
		{
			(style as any).styleSheet.cssText = bodyStyle;
		}
		else
		{
			style.appendChild(frameDocument.createTextNode(bodyStyle));
		}

		frameDocument.head.appendChild(style);
	}

	refineUrl(url: string): string
	{
		if (Type.isStringFilled(url) && /IFRAME/.test(url))
		{
			return Uri.removeParam(url, ['IFRAME', 'IFRAME_TYPE']);
		}

		return url;
	}
}
